import React, { useEffect, useState } from 'react';
import { Button, Typography, Skeleton } from 'antd';
import {
  UserOutlined,
  ShoppingOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import PublicWebContentService from '../services/publicWebContentService';
import './Home.css';
import './HowItWorks.css';

const { Title, Paragraph } = Typography;

const HowItWorks = () => {
  const [loading, setLoading] = useState(true);
  const headline = 'How we work!';
  const [steps, setSteps] = useState([
    {
      title: 'Create Your Account',
      description:
        'Sign up for a free account in just 2 minutes. Provide your basic information and service needs.',
    },
    {
      title: 'Browse Services & Products',
      description:
        'Explore our wide range of services and products. Filter by category, price, or location.',
    },
    {
      title: 'Place Your Order',
      description:
        'Select your desired services or products and complete your order using our secure payment system.',
    },
    {
      title: 'Schedule Service',
      description: 'Choose a convenient time for our technicians to visit your location.',
    },
    {
      title: 'Receive Service/Delivery',
      description:
        'Get your service or delivered products at your convenience. Track your order in real-time.',
    },
  ]);

  // Helper to convert section array to keyed map
  const sectionToMap = (items) => {
    const map = {};
    (items || []).forEach((item) => {
      map[item.content_key] = item.content_value;
    });
    return map;
  };

  useEffect(() => {
    const loadContent = async () => {
      try {
        const pageContent = await PublicWebContentService.getPageContent('how-it-works');
        if (pageContent) {
          const gettingStarted = sectionToMap(pageContent.getting_started);

          const newSteps = [];
          for (let i = 1; i <= 5; i++) {
            const title = gettingStarted[`step_${i}_title`];
            const description = gettingStarted[`step_${i}_description`];
            if (title && description) {
              newSteps.push({ title, description });
            }
          }
          if (newSteps.length > 0) setSteps(newSteps);
        }
      } catch (error) {
        console.error('Failed to load how-it-works content:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '40px' }}>
        <Skeleton active paragraph={{ rows: 12 }} />
      </div>
    );
  }

  const stepIcons = [
    UserOutlined,
    ShoppingOutlined,
    PhoneOutlined,
    CheckCircleOutlined,
    TruckOutlined,
  ];

  return (
    <div className="hiw-page">
      {/* Compact page header */}
      <header className="page-hero">
        <div className="page-hero-inner">
          <h1>{headline}</h1>
        </div>
      </header>

      {/* Numbered step sequence */}
      <section className="home-section">
        <div className="section-header">
          <Title level={2}>Getting Started</Title>
          <Paragraph>
            From browsing to booking, here&apos;s the simple path to getting the help you need.
          </Paragraph>
        </div>
        <ol className="step-list">
          {steps.map((step, index) => {
            const IconComp = stepIcons[index] || UserOutlined;
            return (
              <li className="step-item" key={index}>
                <div className="step-marker">{String(index + 1).padStart(2, '0')}</div>
                <div className="step-card">
                  <div className="step-card-head">
                    <span className="step-card-ic">
                      <IconComp />
                    </span>
                    <h3>{step.title}</h3>
                  </div>
                  <p>{step.description}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      {/* CTA band */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-700 via-brand-600 to-brand-400">
        <div className="mx-auto max-w-content px-6 py-16 text-center">
          <h2 className="font-display text-3xl font-extrabold text-white sm:text-4xl">
            Ready to get started?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-brand-100">
            Join thousands of satisfied customers who trust Current Care for their service needs.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button
              size="large"
              href="/register"
              style={{
                height: 48,
                paddingInline: 26,
                fontWeight: 600,
                background: '#fe9c07',
                borderColor: '#fe9c07',
                color: '#08152b',
              }}
            >
              Create Account
            </Button>
            <Button
              size="large"
              ghost
              href="/contact"
              style={{
                height: 48,
                paddingInline: 24,
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.6)',
              }}
            >
              Schedule Consultation
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
